import { useEffect, useState } from "react";

import { QuestionAnswer } from "@mui/icons-material";
import { Button, Stack, Tooltip, Typography } from "@mui/material";

import { EVOTRUX_BORDER_GREY } from "constants/index";
import { useRoleContext } from "hooks";
import { useUpdateNotifications } from "hooks/useUpdateNotifications";
import {
  formatQuoteAge,
  formatQuoteAgeTooltip,
  roleOwnsLatestQuote,
} from "utils";

import { QuoteOverflowMenu } from "./QuoteOverflowMenu";
import { QuoteStatusBar } from "./QuoteStatusBar";
import { QuoteActionModal } from "../../../../../ShipmentQuote/components/QuoteInteraction/components/QuoteActionModal";

import type { CarrierInsurance } from "types/company";

import type { QuoteViewType } from "../../types";

interface Props {
  quote: ShipperCondensedQuote;
  onQuoteAction: (action: QuoteViewType, quote: ShipperCondensedQuote) => void;
  notificationData: { count: number; notificationIds: number[] };
  insurance: CarrierInsurance | undefined;
}

export function QuoteActionsColumn({
  quote,
  onQuoteAction,
  notificationData,
  insurance,
}: Props) {
  const availableActions = quote.actions || [];
  const { role } = useRoleContext();

  const [selectedAction, setSelectedAction] =
    useState<CondensedQuoteActionValue | null>(null);

  // Extract notification data from context (populated by sidebar's 30-second polling)
  const { count: notificationCount, notificationIds } = notificationData;

  // Local flag to hide badge immediately when user clicks Questions button
  // This provides instant UI feedback while API sync happens in background
  const [preemptivelyMarkedAsRead, setPreemptivelyMarkedAsRead] =
    useState(false);

  const { markNotificationsAsRead } = useUpdateNotifications();

  // Auto-cleanup: Clear preemptive flag when context syncs and notifications are actually read
  // This happens when the sidebar's polling picks up the API changes (within 30 seconds)
  useEffect(() => {
    if (preemptivelyMarkedAsRead && notificationCount === 0) {
      setPreemptivelyMarkedAsRead(false);
    }
  }, [notificationCount, preemptivelyMarkedAsRead]);

  // Check which actions are available based on quote.actions array
  const canDecline =
    availableActions.includes("DECLINE") ||
    availableActions.includes("DECLINE_WITH_FEEDBACK");

  const canBook = availableActions.includes("ACCEPT");

  const secondaryButtons: {
    show: boolean;
    label: string;
    tooltip: string;
    action: CondensedQuoteActionValue;
  }[] = [
    {
      show: availableActions.includes("COUNTER"),
      label: "Counter",
      tooltip: "Send this carrier a different price or dates",
      action: "COUNTER",
    },
    {
      show: canDecline,
      label: "Decline",
      tooltip: "Turn this quote down. The carrier is told it was declined",
      action: availableActions.includes("DECLINE_WITH_FEEDBACK")
        ? "DECLINE_WITH_FEEDBACK"
        : "DECLINE",
    },
    {
      // Only offered once the shipper has countered: the backend lets a party
      // cancel only when the latest action on the quote is their own.
      show: availableActions.includes("CANCEL"),
      label: "Cancel",
      tooltip: "Withdraw your counter offer and end this quote",
      action: "CANCEL",
    },
  ];

  const showUnreadCount = notificationCount > 0 && !preemptivelyMarkedAsRead;

  const handleQuoteQA = () => {
    if (notificationIds.length > 0) {
      // STEP 1: Hide badge immediately for instant user feedback
      // This prevents the 0-30 second delay while waiting for API sync + polling
      setPreemptivelyMarkedAsRead(true);

      // STEP 2: Mark notifications as read in API (background operation)
      // The useEffect above will clear the preemptive flag when this succeeds
      markNotificationsAsRead(notificationIds);
    }

    // Open the QA modal regardless of notification status
    onQuoteAction("QA", quote);
  };

  return (
    <>
      {/* Browsers treat a stated width on an auto-layout table as a suggestion,
      so the cap lives on the cluster itself. */}
      <Stack
        direction="column"
        alignItems="center"
        spacing={1}
        sx={{ width: "100%", maxWidth: 200, ml: "auto" }}
      >
        <QuoteStatusBar
          quoteInternalState={quote.internal_state}
          isLatestQuoteOwnedByRole={!!role && roleOwnsLatestQuote(quote, role)}
        />

        {/* Book stands alone: exactly one filled button, so the eye lands on
        the next move rather than choosing between equals. */}
        {canBook && (
          <Tooltip
            title="Book this carrier at the quoted price and dates"
            describeChild
          >
            <Button
              variant="contained"
              fullWidth
              onClick={() => setSelectedAction("ACCEPT")}
              sx={{ textTransform: "none" }}
            >
              <Typography variant="body2">Book</Typography>
            </Button>
          </Tooltip>
        )}

        {/* The rest, outlined and side by side. */}
        {secondaryButtons.some((actionButton) => actionButton.show) && (
          <Stack direction="row" spacing={0.75} sx={{ width: "100%" }}>
            {secondaryButtons
              .filter((actionButton) => actionButton.show)
              .map((actionButton) => (
                <Tooltip
                  key={actionButton.action}
                  title={actionButton.tooltip}
                  describeChild
                >
                  <Button
                    variant="outlined"
                    onClick={() => setSelectedAction(actionButton.action)}
                    sx={{
                      flex: 1,
                      textTransform: "none",
                      borderColor: EVOTRUX_BORDER_GREY,
                      "&:hover": { backgroundColor: "#f3f4f6" },
                    }}
                  >
                    <Typography color="black" variant="body2">
                      {actionButton.label}
                    </Typography>
                  </Button>
                </Tooltip>
              ))}
          </Stack>
        )}

        {/* Questions button - always visible */}
        <Stack direction="row" spacing={0.75} sx={{ width: "100%" }}>
          <Tooltip
            title="Ask this carrier a question about their quote, or read their answers"
            describeChild
          >
            <Button
              variant="outlined"
              startIcon={<QuestionAnswer sx={{ fontSize: 12 }} />}
              onClick={handleQuoteQA}
              sx={{
                flex: 1,
                minWidth: 0,
                px: 1,
                py: 0.5,
                fontSize: "0.7rem",
                fontWeight: 500,
                textTransform: "none",
                borderColor: EVOTRUX_BORDER_GREY,
                color: "black",
                justifyContent: "flex-start",
              }}
            >
              <Typography variant="body2">
                {showUnreadCount
                  ? `Questions (${notificationCount})`
                  : "Questions"}
              </Typography>
            </Button>
          </Tooltip>
          <QuoteOverflowMenu
            quote={quote}
            insurance={insurance}
            onViewHistory={() => onQuoteAction("HISTORY", quote)}
          />
        </Stack>

        {/* The cluster's quietest line, right-aligned so it reads as an
        annotation rather than a value. */}
        <Tooltip
          title={formatQuoteAgeTooltip(
            quote.created_at,
            quote.internal_state === "COUNTERED",
          )}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              alignSelf: "flex-end",
              cursor: "help",
              lineHeight: 1,
              whiteSpace: "nowrap",
            }}
          >
            {formatQuoteAge(quote.created_at)}
          </Typography>
        </Tooltip>
      </Stack>
      {selectedAction && (
        <QuoteActionModal
          action={selectedAction}
          onClose={() => setSelectedAction(null)}
          quote={quote}
        />
      )}
    </>
  );
}
